import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {ExamModule, StandardSubject, SubjectLevel} from '@cp/model';

interface ExamModuleFormData {
  name: string;
  level: SubjectLevel;
  subject: StandardSubject;
  topics: string[];
  examBoard: string;
  questionsPerTopic: number;
  requiredRole: string;
  attribution: string;
  author: string;
  source: string;
  license: string;
}

interface ExamModuleFormProps {
  open: boolean;
  examModule: ExamModule | null;
  formData: ExamModuleFormData;
  topicsText: string;
  formErrors: {[key: string]: string};
  creating: boolean;
  updating: boolean;
  onClose: () => void;
  onSubmit: (data: ExamModuleFormData, topicsText: string) => void;
  onFormDataChange: (data: ExamModuleFormData) => void;
  onTopicsTextChange: (text: string) => void;
  onClearError: (field: string) => void;
}

export default function ExamModuleForm({
  open,
  examModule,
  formData,
  topicsText,
  formErrors,
  creating,
  updating,
  onClose,
  onSubmit,
  onFormDataChange,
  onTopicsTextChange,
  onClearError,
}: ExamModuleFormProps) {
  const handleSubmit = () => {
    onSubmit(formData, topicsText);
  };

  const isSaving = creating || updating;
  const btnCreateUpdate = examModule ? 'Update' : 'Create';
  const buttonText = isSaving ? 'Saving...' : btnCreateUpdate;
  const isEditing = !!examModule;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>
        {examModule ? 'Edit Exam Module' : 'Create New Exam Module'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mt: 1}}>
          {Object.entries(formErrors).some(([_, error]) => error && error.trim()) && (
            <Alert severity="error" sx={{mb: 1}}>
              Please fix the following errors:
              <ul style={{margin: '8px 0 0 0', paddingLeft: '20px'}}>
                {Object.entries(formErrors)
                  .filter(([_, error]) => error && error.trim())
                  .map(([field, error]) => (
                    <li key={`ExamModuleForm-li-${field}`}>{error}</li>
                  ))}
              </ul>
            </Alert>
          )}

          <TextField
            label="Module Name"
            value={formData.name}
            onChange={(e) => {
              onFormDataChange({...formData, name: e.target.value});
              onClearError('name');
            }}
            fullWidth
            required
            error={!!(formErrors.name && formErrors.name.trim())}
            helperText={formErrors.name && formErrors.name.trim() ? formErrors.name : ''}
          />

          <FormControl fullWidth>
            <InputLabel>Level</InputLabel>
            <Select
              value={formData.level}
              label="Level"
              onChange={(e) => onFormDataChange({...formData, level: e.target.value as SubjectLevel})}
              disabled={isEditing}
            >
              {Object.values(SubjectLevel).map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
            {isEditing && (
              <Typography variant="caption" sx={{color: 'text.secondary', mt: 0.5}}>
                Level cannot be changed after creation
              </Typography>
            )}
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Subject</InputLabel>
            <Select
              value={formData.subject}
              label="Subject"
              onChange={(e) => onFormDataChange({...formData, subject: e.target.value as StandardSubject})}
              disabled={isEditing}
            >
              {Object.values(StandardSubject).map((subject) => (
                <MenuItem key={subject} value={subject}>
                  {subject}
                </MenuItem>
              ))}
            </Select>
            {isEditing && (
              <Typography variant="caption" sx={{color: 'text.secondary', mt: 0.5}}>
                Subject cannot be changed after creation
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Exam Board (Optional)"
            value={formData.examBoard}
            onChange={(e) => onFormDataChange({...formData, examBoard: e.target.value})}
            fullWidth
            placeholder="e.g., AQA, Edexcel, OCR"
            disabled={isEditing}
            helperText={isEditing ? 'Exam board cannot be changed after creation' : ''}
          />

          <TextField
            label="Required Role (Optional)"
            value={formData.requiredRole}
            onChange={(e) => {
              onFormDataChange({...formData, requiredRole: e.target.value});
              onClearError('requiredRole');
            }}
            fullWidth
            placeholder="e.g., admin, teacher, student"
            error={!!(formErrors.requiredRole && formErrors.requiredRole.trim())}
            helperText={
              formErrors.requiredRole && formErrors.requiredRole.trim()
                ? formErrors.requiredRole
                : 'Role required to access this module'
            }
          />

          <TextField
            label="Attribution (Optional)"
            value={formData.attribution}
            onChange={(e) => {
              onFormDataChange({...formData, attribution: e.target.value});
              onClearError('attribution');
            }}
            fullWidth
            placeholder="e.g., Source or credit information"
            error={!!(formErrors.attribution && formErrors.attribution.trim())}
            helperText={
              formErrors.attribution && formErrors.attribution.trim()
                ? formErrors.attribution
                : 'Attribution or credit for the module content'
            }
          />

          <TextField
            label="Author (Optional)"
            value={formData.author}
            onChange={(e) => {
              onFormDataChange({...formData, author: e.target.value});
              onClearError('author');
            }}
            fullWidth
            placeholder="e.g., Author name"
            error={!!(formErrors.author && formErrors.author.trim())}
            helperText={
              formErrors.author && formErrors.author.trim() ? formErrors.author : 'Author of the module content'
            }
          />

          <TextField
            label="Source (Optional)"
            value={formData.source}
            onChange={(e) => {
              onFormDataChange({...formData, source: e.target.value});
              onClearError('source');
            }}
            fullWidth
            placeholder="e.g., Source URL or reference"
            error={!!(formErrors.source && formErrors.source.trim())}
            helperText={
              formErrors.source && formErrors.source.trim() ? formErrors.source : 'Source of the module content'
            }
          />

          <TextField
            label="License (Optional)"
            value={formData.license}
            onChange={(e) => {
              onFormDataChange({...formData, license: e.target.value});
              onClearError('license');
            }}
            fullWidth
            placeholder="e.g., MIT, CC BY 4.0, etc."
            error={!!(formErrors.license && formErrors.license.trim())}
            helperText={
              formErrors.license && formErrors.license.trim() ? formErrors.license : 'License for the module content'
            }
          />

          <TextField
            label="Questions Per Topic"
            type="number"
            value={formData.questionsPerTopic}
            onChange={(e) => {
              onFormDataChange({...formData, questionsPerTopic: parseInt(e.target.value, 10) || 10});
              onClearError('questionsPerTopic');
            }}
            fullWidth
            inputProps={{min: 1}}
            error={!!(formErrors.questionsPerTopic && formErrors.questionsPerTopic.trim())}
            helperText={
              formErrors.questionsPerTopic && formErrors.questionsPerTopic.trim() ? formErrors.questionsPerTopic : ''
            }
          />

          <Divider sx={{my: 1}} />

          <Typography variant="subtitle2" sx={{color: '#1B263B', fontWeight: 600}}>
            Topics Configuration
          </Typography>

          <TextField
            label="Topics (One per line)"
            value={topicsText}
            onChange={(e) => {
              onTopicsTextChange(e.target.value);
              onClearError('topics');
            }}
            fullWidth
            multiline
            rows={8}
            placeholder="Enter topics, one per line&#10;e.g.,&#10;Algebra&#10;Geometry&#10;Statistics"
            required
            error={!!(formErrors.topics && formErrors.topics.trim())}
            helperText={formErrors.topics && formErrors.topics.trim() ? formErrors.topics : ''}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.name || !topicsText.trim() || isSaving}
          sx={{bgcolor: '#1B263B', '&:hover': {bgcolor: '#0f1419'}}}
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
